// Chart registry.
//
// Two pieces:
//   * CHART_BODIES — id → body component. Bodies are stateless visualizations
//     (no card chrome, no header buttons, no fullscreen modal). The container
//     mounts a body inside its own chrome.
//   * VALUE_TYPE_CATALOG — value-type → ordered cycle of chart_ids. First
//     entry is the system default; CyclerButton advances through the cycle.
//
// Adding a chart: write a body, register it here, and append the id to
// every value-type cycle that should expose it.

import LineChartBody from './bodies/LineChartBody';
import StatsPanelBody from './bodies/StatsPanelBody';
import ActivityCalendarBody from './bodies/ActivityCalendarBody';
import EnumPieBody from './bodies/EnumPieBody';
import EnumBarBody from './bodies/EnumBarBody';
import HistogramBody from './bodies/HistogramBody';
import LabelCloudBody from './bodies/LabelCloudBody';
import TopWordsBody from './bodies/TopWordsBody';
import {
  MapSumPieBody, MapCountPieBody, MapCountBarBody,
} from './bodies/MapDistributionBody';
import MapLinesBody from './bodies/MapLinesBody';
import MapKeyBarBody from './bodies/MapKeyBarBody';
import BoolCalendarBody from './bodies/BoolCalendarBody';
import BoolWeeksBody from './bodies/BoolWeeksBody';
import type { ChartBodyProps, ChartId, ValueType } from './types';

export const CHART_BODIES: Record<ChartId, React.FC<ChartBodyProps>> = {
  line: LineChartBody,
  stats: StatsPanelBody,
  activity: ActivityCalendarBody,
  pie: EnumPieBody,
  enum_bar: EnumBarBody,
  histogram: HistogramBody,
  label_cloud: LabelCloudBody,
  top_words: TopWordsBody,
  map_sum_pie: MapSumPieBody,
  map_count_pie: MapCountPieBody,
  map_count_bar: MapCountBarBody,
  map_lines: MapLinesBody,
  map_key_bar: MapKeyBarBody,
  bool_calendar: BoolCalendarBody,
  bool_weeks: BoolWeeksBody,
};

export interface ValueTypeCatalogEntry {
  // Ordered cycle of chart_ids the cycler advances through. The first
  // entry is the hardcoded system default (used when neither a value-
  // type pref nor a per-feature override applies).
  cycle: ChartId[];
}

export const VALUE_TYPE_CATALOG: Record<ValueType, ValueTypeCatalogEntry> = {
  decimal:     { cycle: ['line', 'stats', 'histogram', 'activity'] },
  rating:      { cycle: ['line', 'stats', 'histogram', 'activity'] },
  freeform:    { cycle: ['label_cloud', 'top_words', 'activity'] },
  enum:        { cycle: ['pie', 'enum_bar', 'activity'] },
  // Yes/no: the Yes/No/not-recorded calendar first, then Yes and No days
  // per week. Unrecorded days are never drawn as No in either.
  bool:        { cycle: ['bool_calendar', 'bool_weeks', 'activity'] },
  reference:   { cycle: ['activity'] },
  list:        { cycle: ['activity'] },
  // Non-numeric map: only count-based distribution makes sense.
  map:         { cycle: ['map_count_pie', 'map_count_bar', 'activity'] },
  // Numeric map: total-by-key as the default, with the per-key
  // time-series as the secondary. Other slices (bar variant, counts,
  // activity) didn't add much and just made the cycle long.
  map_numeric: { cycle: ['map_sum_pie', 'map_lines'] },
  // Orderable numeric key + numeric value: the key is the x-axis, so an
  // avg-per-key bar chart is the natural primary (bars because the keys
  // are discrete — a line would imply smooth interpolation between
  // adjacent integers). Per-key time series stays as the secondary.
  // Sum-donut is omitted here — it discards the key's ordering, which
  // is the whole point of this bucket.
  map_ordered_numeric: { cycle: ['map_key_bar', 'map_lines'] },
  other:       { cycle: ['activity'] },
};

// Convenience helper used in resolve() callsites.
export function systemDefaultFor(valueType: ValueType): ChartId {
  return VALUE_TYPE_CATALOG[valueType].cycle[0];
}

// Four title shapes:
//   * 'over-time'    — y is the feature's measured value (with units), x is
//     time. Suffix reads `${unit} vs. time`.
//   * 'count'        — y is the count of checkins, x is a feature value bin
//     or category. Suffix reads `checkins vs. ${unit}` (or just
//     `checkins` for unit-less features).
//   * 'summary'      — no two-axis plot at all (stats panel). Suffix is just
//     `${unit}`, or nothing.
//   * 'map-count'    — count of map pairs grouped by key. X-axis is the
//     key, not the feature unit. Suffix reads `pairs by key`.
//   * 'map-sum'      — sum of the value half grouped by key. Suffix reads
//     `${unit} by key` (or `value by key`).
//   * 'map-lines'    — value half traced per key over time. Suffix reads
//     `${unit} by key over time`.
//   * 'map-key-bar'  — value-half average by ordered numeric key. Suffix
//     reads `avg ${unit} by key` (or `avg by key`).
//   * 'bool-days'    — yes/no answers per day. No suffix: the body's own
//     header and legend name the answers.
//   * 'bool-weeks'   — yes/no days per week. Suffix reads `days per week`.
type ChartTitleKind =
  | 'over-time' | 'count' | 'summary'
  | 'map-count' | 'map-sum' | 'map-lines' | 'map-key-bar'
  | 'bool-days' | 'bool-weeks';

const CHART_TITLE_KIND: Record<ChartId, ChartTitleKind> = {
  line: 'over-time',
  stats: 'summary',
  activity: 'count',
  pie: 'count',
  enum_bar: 'count',
  histogram: 'count',
  label_cloud: 'count',
  top_words: 'count',
  map_sum_pie: 'map-sum',
  map_count_pie: 'map-count',
  map_count_bar: 'map-count',
  map_lines: 'map-lines',
  map_key_bar: 'map-key-bar',
  bool_calendar: 'bool-days',
  bool_weeks: 'bool-weeks',
};

export function chartTitleSuffix(chartId: ChartId, unit?: string): string | null {
  const kind = CHART_TITLE_KIND[chartId];
  if (kind === 'summary') return unit ?? null;
  if (kind === 'over-time') return unit ? `${unit} vs. time` : null;
  if (kind === 'map-count') return 'pairs by key';
  if (kind === 'map-sum') return 'totals';
  if (kind === 'map-lines') return 'vs. time';
  if (kind === 'bool-days') return null;
  if (kind === 'bool-weeks') return 'days per week';
  if (kind === 'map-key-bar') return unit ? `avg ${unit} by key` : 'avg by key';
  return unit ? `checkins vs. ${unit}` : 'checkins';
}
